import {
    createRouter,
    createWebHistory,
    type RouteLocationNormalized,
    type RouteRecordRaw
} from 'vue-router';
import { h, defineComponent, provide, type Component } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useModulesStore } from '@/stores/modules';
import { useToastStore } from '@/stores/toast';
import { i18n, translateLabel } from '@/i18n';
import { loadModuleBundleManifest, mergeModuleI18n } from '@/composables/useModules';
import { ModuleContextKey, type ModuleContext, type ModuleToast, type ModuleAuth, type ModuleTranslate } from '@/shared/module-context';
import { fleetApi } from '@/api/fleet';

/** Базовые routes shell (не из манифестов). */
const baseRoutes: RouteRecordRaw[] = [
    {
        path: '/',
        redirect: '/dashboard'
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('@/views/LoginView.vue'),
        meta: { standalone: true, public: true }
    },
    {
        path: '/setup',
        name: 'setup',
        component: () => import('@/views/SetupView.vue'),
        meta: { standalone: true, public: true, title: 'Setup' }
    },
    {
        path: '/modules',
        name: 'modules',
        component: () => import('@/views/ModuleCatalogView.vue'),
        meta: { title: 'modules.title' }
    },
    {
        path: '/nodes',
        name: 'nodes',
        component: () => import('@/views/NodesView.vue'),
        meta: { title: 'nodes.title' }
    },
    {
        path: '/nodes/:nodeId',
        name: 'node-detail',
        component: () => import('@/views/NodeDetailView.vue'),
        meta: { title: 'nodes.title' }
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { title: 'settings.title' }
    },
    {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: 'dashboard.title', fullBleed: true }
    },
    {
        path: '/fleet/graph',
        name: 'fleet.graph',
        component: () => import('@/views/GraphEditorView.vue'),
        meta: { title: 'graph.title', fullBleed: true }
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/views/NotFoundView.vue'),
        meta: { standalone: true }
    }
];

export const router = createRouter({
    history: createWebHistory(),
    routes: baseRoutes
});

/**
 * Динамически регистрирует routes из загруженных модульных манифестов.
 * Вызывается после загрузки манифестов (useModulesStore.load()).
 *
 * Module frontend bundles загружаются lazy через dynamic import.
 * Если bundle не указан — route рендерит placeholder-компонент.
 */
export async function registerModuleRoutes(): Promise<void> {
    const modules = useModulesStore();

    for (const manifest of modules.manifests) {
        for (const route of manifest.routes) {
            const routeName = `${manifest.id}.${route.name}`;
            const fullPath = route.path.startsWith('/') ? route.path : `/${route.path}`;

            // Пропускаем, если route уже зарегистрирован (защита от дублей).
            if (router.hasRoute(routeName)) continue;

            const record: RouteRecordRaw = {
                path: fullPath,
                name: routeName,
                meta: {
                    title: route.title,
                    adminOnly: route.adminOnly,
                    moduleId: manifest.id
                },
                props: true,
                component: () => loadRouteComponent(manifest.id, route.name)
            };

            router.addRoute(record);
        }
    }
}

let moduleInitialization: Promise<void> | null = null;

/** Registers routes without downloading inactive modules. */
export async function initializeModuleRoutes(force = false): Promise<void> {
    if (force) moduleInitialization = null;
    if (moduleInitialization) return moduleInitialization;

    moduleInitialization = (async () => {
        const modules = useModulesStore();
        await modules.load(force);

        await registerModuleRoutes();
    })();

    try {
        await moduleInitialization;
    } finally {
        if (useModulesStore().error) moduleInitialization = null;
    }
}

/** Загруженные CSS-файлы модулей (чтобы не загружать повторно). */
const loadedCss = new Map<string, Promise<void>>();

/** Загружает CSS для модуля, если ещё не загружен. */
function loadModuleCss(frontendBundle: string, version: string): Promise<void> {
    const url = new URL('./style.css', new URL(frontendBundle, window.location.href));
    url.searchParams.set('v', version);
    const key = url.href;
    const cached = loadedCss.get(key);
    if (cached) return cached;
    const promise = new Promise<void>((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = key;
        const timeout = window.setTimeout(() => fail(), 15_000);
        const fail = () => {
            window.clearTimeout(timeout);
            link.remove();
            reject(new Error('Не удалось загрузить стили модуля'));
        };
        link.onload = () => { window.clearTimeout(timeout); resolve(); };
        link.onerror = fail;
        document.head.appendChild(link);
    });
    loadedCss.set(key, promise);
    promise.catch(() => loadedCss.delete(key));
    return promise;
}

/**
 * Загружает компонент для route из frontend bundle модуля.
 * Если bundle недоступен — возвращает placeholder.
 */
async function loadRouteComponent(moduleId: string, routeName: string) {
    const modules = useModulesStore();
    const manifest = modules.manifests.find((m) => m.id === moduleId);

    if (!manifest?.frontendBundle) {
        // Модуль без UI — placeholder.
        return definePlaceholder(`Модуль «${manifest?.displayName ?? moduleId}» не имеет UI`);
    }

    try {
        // Загружаем CSS для модуля (style.css из wwwroot).
        const [bundle] = await Promise.all([
            loadModuleBundleManifest(manifest),
            loadModuleCss(manifest.frontendBundle, manifest.version)
        ]);
        if (!bundle) return definePlaceholder(`Модуль «${manifest.displayName}» не имеет UI`);
        mergeModuleI18n(bundle);

        const component = bundle.components?.[routeName];
        if (!component) {
            return definePlaceholder(`Компонент ${routeName} не найден в bundle модуля ${moduleId}`);
        }

        // Module bundle имеет свой pinia instance (не externalize).
        // Оборачиваем компонент в wrapper, который предоставляет module context
        // (toast, auth) через provide/inject — вместо pinia stores.
        return defineModuleWrapper(component);
    } catch (error) {
        console.error(`Module ${moduleId} failed to load`, error);
        return definePlaceholder(`Не удалось загрузить модуль ${moduleId}`, true);
    }
}

/**
 * Wrapper-компонент для module bundle компонентов.
 * Предоставляет ModuleContext (toast, auth) через provide/inject,
 * чтобы module компоненты могли использовать toast и auth без pinia stores.
 */
function defineModuleWrapper(component: Component) {
    return defineComponent({
        name: 'ModuleWrapper',
        inheritAttrs: false,
        setup(_, { attrs }) {
            const toast = useToastStore();
            const auth = useAuthStore();

            const moduleToast: ModuleToast = {
                push: (kind, title, message) => toast.push(kind, title, message),
                success: (title, message) => toast.push('success', title, message),
                error: (title, message) => toast.push('error', title, message),
                info: (title, message) => toast.push('info', title, message),
                warn: (title, message) => toast.push('warn', title, message)
            };

            const moduleAuth: ModuleAuth = {
                get user() { return auth.user; },
                get isAuthenticated() { return auth.isAuthenticated; },
                get canAdmin() { return auth.canAdmin; },
                get canMutate() { return auth.canMutate; }
            };

            const moduleI18n: ModuleTranslate = {
                t: (key: string, named?: Record<string, unknown>) => {
                    // Read reactive locale.value to trigger re-evaluation on language change.
                    // This makes t() reactive when called from Vue templates.
                    void i18n.global.locale.value;
                    return i18n.global.t(key, named ?? {});
                },
                get locale() {
                    return i18n.global.locale.value;
                }
            };

            const ctx: ModuleContext = { toast: moduleToast, auth: moduleAuth, i18n: moduleI18n };
            provide(ModuleContextKey, ctx);

            return () => h(component, attrs);
        }
    });
}

/** Создаёт простой placeholder-компонент для модулей без UI. */
function definePlaceholder(message: string, retry = false) {
    return {
        name: 'ModulePlaceholder',
        setup() {
            return () =>
                h('div', { class: 'module-placeholder' }, [
                    h('p', { class: 'module-placeholder__msg' }, message),
                    retry ? h('button', { class: 'btn-cta', onClick: () => window.location.reload() }, 'Повторить загрузку') : null
                ]);
        }
    };
}

// Импорты для placeholder-компонента и module wrapper — в начале файла.
// (см. imports выше)

router.beforeEach(async (to: RouteLocationNormalized) => {
    const auth = useAuthStore();

    if (to.name === 'login') {
        if (auth.isAuthenticated) {
            return { name: 'dashboard' };
        }
        const returnTo = typeof to.query.returnTo === 'string' ? to.query.returnTo : '/dashboard';
        auth.login(returnTo);
        return false;
    }

    // Setup page is public — always accessible
    if (to.name === 'setup') return true;

    if (!auth.isAuthenticated) {
        return { name: 'login', query: { returnTo: to.fullPath } };
    }

    if (to.name === 'not-found') {
        const modules = useModulesStore();
        await initializeModuleRoutes(!modules.loaded || modules.error !== null);
        if (router.resolve(to.fullPath).name !== 'not-found') return to.fullPath;
    }

    // Auto-redirect to Setup if not completed (check once per session)
    if (!setupChecked) {
        setupChecked = true;
        try {
            const status = await fleetApi.getSetupStatus();
            setupDone = status.completed;
        } catch {
            // Setup endpoint not available — assume done
            setupDone = true;
        }
    }
    if (!setupDone && to.name !== 'setup') {
        return { name: 'setup' };
    }

    if (to.meta.adminOnly && !auth.canAdmin) {
        return { name: 'dashboard' };
    }
    return true;
});

// Setup status cache (checked once per session)
let setupChecked = false;
let setupDone = true;

/** Reset setup cache — call after setup completes to allow navigation. */
export function resetSetupCache() {
    setupChecked = true;
    setupDone = true;
}

router.afterEach((to) => {
    const title = (to.meta.title as string | undefined) ?? '';
    const moduleId = to.meta.moduleId as string | undefined;
    const fallback = title.endsWith('.title')
        ? useModulesStore().manifests.find(manifest => manifest.id === moduleId)?.displayName
        : undefined;
    document.title = title ? `${translateLabel(title, fallback)} · CaviCode Network` : 'CaviCode Network';
});
